import { _ } from "../../../../core/polyglot/polyglot.js";
import { formatCurrency } from "../../../../core/helpers/currency-formatting-util.js";
import { getCSSVariable } from "../../../../core/helpers/css-util.js";
import { getZeroPositionRatio } from "../../../../core/helpers/chart-util.js";
import AbstractChartStrategy from "../../../../core/widgets/ChartWidget/AbstractChartStrategy.js";

const outgoingId = "outgoing";
const cashFlowSumId = "cash-flow-sum";
const ingoingId = "ingoing";

/**
 * Implement a Strategy to render the graph on top of the Cash Flow report.
 */
export default class CashFlowChartStrategy extends AbstractChartStrategy {
	constructor() {
		super(...arguments);

		this._incomeValues = [];
		this._costValues = [];
		this._minValue = Number.MAX_VALUE;
		this._maxValue = Number.MIN_VALUE;
	}

	_makeDatasets({ context, values }) {
		return [
			this._makeLineSeries({ context, values }),
			...this._makeBarsSeries({ values }),
		];
	}

	_makeLineSeries({ values }) {
		const lineValues = values.get("line");

		const data = lineValues.map((keyFigureValue) => {
			const value = keyFigureValue.getValue();

			if (value > this._maxValue) {
				this._maxValue = value;
			}

			if (value < this._minValue) {
				this._minValue = value;
			}

			return value;
		});

		this._zeroPositionRatio = getZeroPositionRatio({ data });

		return {
			type: "line",
			label: _("Cash balance"),
			id: cashFlowSumId,
			data,
			pointStyle: "circle",
			borderWidth: 2,
			pointRadius: 4,
			pointBorderWidth: 2,
			pointHoverRadius: 6,
			pointBackgroundColor: ({ raw }) =>
				raw < 0 && raw === this._minValue
					? this._getNegativeColor()
					: "white",
			borderColor: ({ raw }) =>
				raw < 0 ? this._getNegativeColor() : "black",
		};
	}

	_makeBarsSeries({ values }) {
		const { incomeData, costData } = this._makeBarsData({ values });

		return [
			{
				type: "bar",
				label: _("Outgoing"),
				id: outgoingId,
				backgroundColor: this._getOutgoingColor(),
				borderColor: this._getOutgoingColor(),
				data: costData,
				barThickness: 4,
				borderRadius: {
					bottomLeft: Number.MAX_VALUE,
					bottomRight: Number.MAX_VALUE,
				},
				borderSkipped: false,
			},
			{
				type: "bar",
				label: _("Ingoing"),
				id: ingoingId,
				backgroundColor: this._getIngoingColor(),
				borderColor: this._getIngoingColor(),
				borderRadius: {
					topLeft: Number.MAX_VALUE,
					topRight: Number.MAX_VALUE,
				},
				data: incomeData,
				barThickness: 4,
				borderSkipped: false,
			},
		];
	}

	_makeBarsData({ values }) {
		const lineValues = values.get("line");

		const incomeData = [];
		const costData = [];

		const { maxIncome, minCost } = this._getBarsValues({ values });

		const lineDelta = this._maxValue - this._minValue;
		const barsDelta = maxIncome - minCost;

		const factor = lineDelta / barsDelta;

		lineValues.forEach((keyFigureValue, index) => {
			const value = keyFigureValue.getValue();

			incomeData.push([
				value,
				value + this._incomeValues[index] * factor,
			]);

			costData.push([value + this._costValues[index] * factor, value]);
		});

		return { incomeData, costData };
	}

	_getBarsValues({ values }) {
		const lineValues = values.get("line");
		const barsValues = values.get("bars");
		let maxIncome = 0;
		let minCost = 0;

		lineValues.forEach((__, index) => {
			let incomeValue = 0;
			let costValue = 0;

			barsValues.forEach((bar) => {
				const barValue = bar[index];
				if (barValue >= 0) {
					incomeValue += barValue;
				} else {
					costValue += barValue;
				}
			});

			if (costValue < minCost) {
				minCost = costValue;
			}

			if (incomeValue > maxIncome) {
				maxIncome = incomeValue;
			}

			this._incomeValues.push(incomeValue);
			this._costValues.push(costValue);
		});

		return { minCost, maxIncome };
	}

	_getChartOptions({ context: mainContext }) {
		return {
			...super._getChartOptions(),
			fill: {
				target: "origin",
				above: this._makePositiveGradient({ context: mainContext }),
				below: this._makeNegativeGradient({ context: mainContext }),
			},
			scales: {
				x: { offset: true, display: false, stacked: true },
				y: {
					grid: {
						drawBorder: false,
						drawTicks: false,
						borderDash: (context) =>
							this._script({
								inputValue: context.tick.value,
								value: [4, 4],
								defaultValue: [],
								minMaxOnly: true,
							}),
						color: (context) =>
							this._script({
								inputValue: context.tick.value,
								value:
									context.tick.value >= 0
										? this._getPositiveColor()
										: this._getNegativeColor(),
								defaultValue: "#bbb",
								minMaxOnly: true,
							}),
						lineWidth: (context) =>
							this._script({
								inputValue: context.tick.value,
								value: 1,
								defaultValue: 0,
							}),
					},
					beginAtZero: true,
					afterBuildTicks: (axis) => {
						const ticks = [0, this._minValue, this._maxValue];
						ticks.sort();

						axis.ticks = ticks.map((value) => ({
							value,
						}));
					},
					ticks: {
						display: false,
					},
				},
			},
			plugins: {
				tooltip: {
					filter: (toolTipItem) =>
						toolTipItem.dataset.id === cashFlowSumId,
					callbacks: {
						label: (context) =>
							this._makeTooltipBody({
								datasets: context.chart.data.datasets,
								dataIndex: context.dataIndex,
							}),
					},
				},
			},
		};
	}

	_makeTooltipBody({ datasets, dataIndex }) {
		const lineSeries = this._getDatasetById({
			datasets,
			id: cashFlowSumId,
		});
		const ingoingSeries = this._getDatasetById({ datasets, id: ingoingId });
		const outgoingSeries = this._getDatasetById({
			datasets,
			id: outgoingId,
		});
		const ingoingValue = this._incomeValues[dataIndex];
		const outgoingValue = -this._costValues[dataIndex];

		return [
			`${lineSeries.label}: ${formatCurrency(
				lineSeries.data[dataIndex],
			)}`,
			`${ingoingSeries.label}: ${formatCurrency(ingoingValue)}`,
			`${outgoingSeries.label}: ${formatCurrency(outgoingValue)}`,
		];
	}

	_getDatasetById({ datasets, id }) {
		return datasets.find((dataset) => dataset.id === id);
	}

	_script({ inputValue, minMaxOnly = false, value, defaultValue }) {
		if (inputValue === 0) return minMaxOnly ? defaultValue : value;
		if (inputValue === this._maxValue) return value;
		if (inputValue === this._minValue) return value;
		return defaultValue;
	}

	_getPositiveColor() {
		return getCSSVariable("--cash-flow-positive");
	}

	_getIngoingColor() {
		return getCSSVariable("--cash-flow-ingoing");
	}

	_getOutgoingColor() {
		return getCSSVariable("--cash-flow-outgoing");
	}

	_getPositiveGradientColor() {
		return getCSSVariable("--cash-flow-positive-gradient");
	}

	_getPositiveEndPointForYAxis() {
		return getCSSVariable("--cash-flow-positive-end-point");
	}
}
